import { focus, svgColor, svgUri, visuallyHidden } from '../../helpers'
import { StyleFn } from '..'

export const IconCheck = {
  data: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4 12.6111L8.92308 17.5L20 6.5" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>',
}
export const IconFiberManualRecord = {
  data: '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#000000" d="M8 3a5 5 0 100 10A5 5 0 008 3z"></path></g></svg>',
}
export const IconRemove = {
  data: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g id="Edit / Remove_Minus"> <path id="Vector" d="M6 12H18" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g> </g></svg>',
}
type ToggleableOptions = {
  /** @internal this override allows the fn to be reused with radios */
  borderRadius?: string | 0 | undefined
  /** @internal this override allows the fn to be reused with radios */
  symbolIcon?: 'check' | 'radio'
}

export const toggleable: (options: ToggleableOptions) => StyleFn =
  ({ borderRadius = '3px', symbolIcon = 'check' } = {}) =>
  (theme) => {
    const {
      color,
      font,
      opacity,
      size: { spacing },
    } = theme
    const { focusWithPseudo } = focus(theme)

    const symbolSVG = symbolIcon === 'check' ? IconCheck : IconFiberManualRecord
    const colors = {
      text: color.getModalValues(color.text.secondary),
      unchecked: {
        border: color.getModalValues(color.border.interactive),
        background: 'transparent',
      },
      checked: {
        border:
          color.type === 'light'
            ? color.background.interactive.primary.small
            : color.background.interactive.primaryReverse.small,
        background:
          color.type === 'light'
            ? color.background.interactive.primary.small
            : color.background.interactive.primaryReverse.small,
        icon:
          color.type === 'light'
            ? color.background.interactive.primaryReverse.small
            : color.background.interactive.primary.small,
      },
    }

    const labelCheckedStyles = {
      fontWeight: font.weight.medium,
      // FIXME workaround for checked medium weight changing size of the text
      // We should implement a real fix like this : https://codepen.io/hexagoncircle/pen/WNrYPLo
      // but we need to refactor the component as ":after" is used extensively
      letterSpacing: -0.07,
      '&:before': {
        border: colors.checked.border,
        background: colors.checked.background,
      },
      '&:hover:before': {
        background: colors.checked.background,
      },
      '&:hover:after': {
        opacity: 0.7,
      },
    }

    return {
      root: {
        ...focusWithPseudo({
          focusSelector: 'has(input:focus-visible)',
          width: 24,
          radius: borderRadius !== '3px' ? borderRadius : '6px',
        }),
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        color: colors.text,
        '& > $label:before': {
          background: colors.unchecked.background,
          border: `1px solid ${colors.unchecked.border}`,
        },
        '& > $input:checked + $label:after': {
          backgroundImage: svgUri(svgColor(symbolSVG.data, colors.checked.icon)),
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'left 50% top 50%',
          ...(symbolIcon === 'radio' && { backgroundSize: spacing[8] }),
        },
        '& > $input:checked:not(:disabled) + $label:hover:after': {
          opacity: 0.7,
        },
        '& > $input:checked + $label': labelCheckedStyles,
      },
      _indeterminate: {
        '& $label': labelCheckedStyles,
        '& $label:after': {
          backgroundImage: `${svgUri(svgColor(IconRemove.data, colors.checked.icon))} !important`,
        },
      },
      _medium: {},
      _large: {
        '& > $label': {
          fontSize: font.size.medium,
          lineHeight: font.lineheight.medium,
        },
        '& > $label:before': {
          width: spacing[24],
          height: spacing[24],
        },
        '& > $input:checked + $label:after': {
          width: spacing[24],
          height: spacing[24],
          backgroundSize: spacing[24],
          ...(symbolIcon === 'radio' && { backgroundSize: spacing[12] }),
        },
        '&:has(input:focus-visible):after': {
          width: 32,
        },
      },
      // FIXME usage of this class is unclear (react radio ?)
      _icon: {
        '& > $input': {
          width: '100%',
          height: '100%',
          clip: 'auto',
          opacity: 0,
        },
        '& > $label:before, & > $label:after': {
          display: 'none',
        },
        '& > svg': {
          marginRight: spacing[8],
        },
      },
      input: {
        position: 'absolute',
        ...visuallyHidden(),
        '&:disabled + $label': {
          opacity: opacity.disabled,
        },
        '&:disabled + $label:hover': {
          cursor: 'not-allowed',
        },
        '& + $label:after': {
          content: "''",
          position: 'absolute',
          top: 0,
          left: 0,
          width: spacing[16],
          height: spacing[16],
          backgroundSize: spacing[16],
        },
      },
      label: {
        display: 'inline-flex',
        position: 'relative',
        margin: 0,
        padding: 0,
        fontSize: font.size.small,
        lineHeight: font.lineheight.xxsmall,
        '&:hover': {
          cursor: 'pointer',
        },
        '&:before': {
          content: "''",
          position: 'relative',
          display: 'inline-block',
          flexShrink: 0,
          width: spacing[16],
          height: spacing[16],
          marginRight: spacing[8],
          borderRadius,
        },
      },
    }
  }

export const Checkbox: StyleFn = toggleable({})
